import {ModuleFederationRuntimePlugin} from '@module-federation/runtime';
import {RemoteWithEntry} from '@module-federation/sdk';
import {getThothOverride, storeThothOverrideFromUrl} from '@/helpers/thoth_override_helpers';

/**
 * Runtime plugin that allows QA to override the Thoth remote module URL
 * via a query parameter, enabling testing against ephemeral PR environments.
 *
 * Usage: Navigate to https://careco.qa.hbu.co/?thoth_remote=pr-42
 * The override persists in sessionStorage for the browser session.
 * Close the tab to reset.
 *
 * Security: gated on REACT_APP_ALLOW_THOTH_OVERRIDE, which is only set in qa and qa2.
 * The value is substituted into the bundle at container start, so the guard is evaluated
 * at runtime — see thoth_override_helpers for why it must not be a foldable comparison.
 */
const isThothRemote = (remote: {name: string; alias?: string}): boolean =>
  remote.name === 'thoth' || remote.alias === 'thoth' || remote.name.endsWith('__thoth');

const overrideEntry = (override: string): string =>
  `https://thoth-assets-${override}.ob.qa.hbu.co/build/mf-manifest.json`;

export default function (): ModuleFederationRuntimePlugin {
  return {
    name: 'thoth-override',
    beforeInit(args) {
      storeThothOverrideFromUrl();

      const override = getThothOverride();
      if (override) {
        const remote = (args.userOptions.remotes || [])
          .filter((r): r is RemoteWithEntry => 'entry' in r)
          .find(isThothRemote);

        if (remote) {
          remote.entry = overrideEntry(override);
        }
      }

      return args;
    },
    // @module-federation/vite >= 1.16 registers remotes per loadRemote chunk with the
    // build-time entry baked in (under a prefixed name), bypassing beforeInit's
    // userOptions rewrite — this hook fires on every registration path.
    beforeRegisterRemote(args) {
      const override = getThothOverride();
      if (override && 'entry' in args.remote && isThothRemote(args.remote)) {
        args.remote.entry = overrideEntry(override);
      }

      return args;
    },
  };
}
